import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Camera, Loader2, ImageOff, Video, VideoOff } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import EditableText from '../components/EditableText';
import { toEmbedUrl } from '../lib/embedUrl';

/**
 * Shared layout for every Giving sub-page (Online, Automatic Debits,
 * Weekly Envelopes). Mirrors the ministry-detail layout:
 *   1) slim purple band with the page title
 *   2) two-column body — admin-editable text on the left, admin-managed
 *      photo OR video on the right (video wins when both are set)
 *   3) standard "Contact the Parish Office" CTA at the bottom
 */
const GivePage = ({ slug, title, contentKey, defaultBody, fallbackImage, contactDescription }) => {
  const { isAdmin } = useAuth();
  const { toast } = useToast();
  const [imgMeta, setImgMeta] = useState({ exists: false, updated_at: 0 });
  const [metaLoaded, setMetaLoaded] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [savingVideo, setSavingVideo] = useState(false);
  const fileInputRef = useRef(null);
  const videoContentKey = `${slug}_video_url`;

  useEffect(() => {
    let cancelled = false;
    setMetaLoaded(false);
    // Load image meta + video URL in parallel
    Promise.all([
      api.getPageImageMeta(slug).catch(() => ({ data: { exists: false, updated_at: 0 } })),
      api.getContent(videoContentKey).catch(() => ({ data: { value: '' } })),
    ]).then(([imgRes, vidRes]) => {
      if (cancelled) return;
      setImgMeta(imgRes.data);
      setVideoUrl((vidRes.data?.value || '').trim());
      setMetaLoaded(true);
    });
    return () => { cancelled = true; };
  }, [slug, videoContentKey]);

  // Never emit the fallback URL before metaLoaded — otherwise the stock
  // photo would flash before the admin-uploaded photo swaps in.
  const imgSrc = !metaLoaded
    ? null
    : imgMeta.exists
      ? api.pageImageUrl(slug, imgMeta.updated_at)
      : (fallbackImage || null);
  const embedUrl = toEmbedUrl(videoUrl);
  const hasMedia = !!(embedUrl || imgSrc);

  const handleFile = async (file) => {
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/i.test(file.type)) {
      toast({ title: 'Please choose a JPEG, PNG, or WebP image', variant: 'destructive' });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: 'Image is larger than 10 MB', variant: 'destructive' });
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.uploadPageImage(slug, fd);
      setImgMeta(data);
      toast({ title: 'Photo uploaded' });
    } catch (e) {
      toast({ title: 'Upload failed', description: e?.response?.data?.detail || String(e), variant: 'destructive' });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemovePhoto = async () => {
    setUploading(true);
    try {
      await api.deletePageImage(slug);
      setImgMeta({ exists: false, updated_at: 0 });
      toast({ title: 'Photo removed' });
    } catch (e) {
      toast({ title: 'Remove failed', description: e?.response?.data?.detail || String(e), variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const handleSetVideoUrl = async () => {
    const next = window.prompt(
      'Paste a YouTube or Vimeo URL (e.g. https://youtu.be/xxxxxxxx). Leave blank to remove the video.',
      videoUrl,
    );
    if (next === null) return; // cancelled
    const trimmed = next.trim();
    setSavingVideo(true);
    try {
      await api.updateContent(videoContentKey, trimmed);
      setVideoUrl(trimmed);
      toast({ title: trimmed ? 'Video URL saved' : 'Video URL cleared' });
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || String(e), variant: 'destructive' });
    } finally {
      setSavingVideo(false);
    }
  };

  return (
    <>
      {/* 1) Slim color band header */}
      <section
        className="bg-[#3a2359] py-8 md:py-10 border-b border-[#2a1a45]"
        data-testid={`give-band-${slug}`}
      >
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h1 className="font-display text-3xl md:text-4xl text-white leading-tight">{title}</h1>
        </div>
      </section>

      {/* 2) Two-column body */}
      <section className="max-w-5xl mx-auto px-6 pt-10 pb-14">
        <div className={`grid gap-10 md:gap-12 ${hasMedia || isAdmin ? 'md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]' : ''}`}>
          {/* Left column — editable text */}
          <div className="min-w-0">
            <h2 className="font-display text-2xl md:text-3xl text-[#2a1a45] mb-4">About {title}</h2>
            <div className="prose max-w-none">
              <EditableText
                contentKey={contentKey}
                label={`${title} — Body Text`}
                rows={12}
                defaultValue={defaultBody}
                paragraphClass="text-[15px] md:text-base text-[#3f3a34] leading-[1.85]"
              />
            </div>
          </div>

          {/* Right column — photo or video */}
          {(hasMedia || isAdmin) && (
            <aside className="md:sticky md:top-32 self-start space-y-4" data-testid={`give-media-${slug}`}>
              {!metaLoaded ? (
                <div className="w-full aspect-[4/3] bg-[#faf7f0]" data-testid={`give-image-loading-${slug}`} />
              ) : embedUrl ? (
                <div className="relative aspect-video overflow-hidden bg-black">
                  <iframe
                    src={embedUrl}
                    title={`${title} video`}
                    className="absolute inset-0 w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  />
                </div>
              ) : imgSrc ? (
                <div className="relative overflow-hidden">
                  <img
                    src={imgSrc}
                    alt={title}
                    className="w-full h-auto max-h-[520px] object-cover"
                  />
                </div>
              ) : (
                isAdmin && (
                  <div className="w-full aspect-[4/3] flex flex-col items-center justify-center gap-2 bg-[#faf7f0] border border-dashed border-[#c8b785] text-[#a68a5b] text-xs">
                    <Camera size={28} strokeWidth={1.4} />
                    <p>No photo or video yet</p>
                  </div>
                )
              )}

              {isAdmin && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => handleFile(e.target.files?.[0])}
                    data-testid={`give-file-input-${slug}`}
                  />
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      data-testid={`give-change-photo-${slug}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#3a2359] text-[#3a2359] bg-white hover:bg-[#3a2359] hover:text-white text-xs font-medium disabled:opacity-70"
                    >
                      {uploading ? <><Loader2 size={12} className="animate-spin" /> Uploading…</> : <><Camera size={12} /> {imgMeta.exists ? 'Change' : 'Upload'} photo</>}
                    </button>
                    {imgMeta.exists && (
                      <button
                        type="button"
                        onClick={handleRemovePhoto}
                        disabled={uploading}
                        data-testid={`give-remove-photo-${slug}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#8a2b1a] text-[#8a2b1a] bg-white hover:bg-[#8a2b1a] hover:text-white text-xs font-medium disabled:opacity-70"
                      >
                        <ImageOff size={12} /> Remove photo
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={handleSetVideoUrl}
                      disabled={savingVideo}
                      data-testid={`give-set-video-${slug}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#3a2359] text-[#3a2359] bg-white hover:bg-[#3a2359] hover:text-white text-xs font-medium disabled:opacity-70"
                    >
                      {savingVideo ? <><Loader2 size={12} className="animate-spin" /> Saving…</> : (videoUrl ? <><VideoOff size={12} /> Change video URL</> : <><Video size={12} /> Set video URL</>)}
                    </button>
                  </div>
                  {(videoUrl && !embedUrl) && (
                    <p className="text-xs text-[#8a2b1a] flex items-start gap-1.5">
                      <Video size={12} className="mt-0.5 flex-shrink-0" />
                      URL is set but not a supported YouTube/Vimeo link.
                    </p>
                  )}
                  <p className="text-[11px] text-[#a68a5b] tracking-wide">
                    Video takes priority over the photo when both are set.
                  </p>
                </>
              )}
            </aside>
          )}
        </div>
      </section>

      {/* 3) Standard Contact the Parish Office CTA */}
      <section className="max-w-5xl mx-auto px-6 pb-16" data-testid={`give-contact-${slug}`}>
        <div className="bg-[#f2ead6]/60 border border-[#e9e0cc] p-8 text-center">
          <h3 className="font-display text-2xl text-[#2a1a45] mb-2">Questions or need help?</h3>
          <p className="text-sm text-[#5a534a] mb-5">
            {contactDescription || "Reach out to the parish office and we'll be happy to walk you through your giving options."}
          </p>
          <Link
            to="/contact"
            className="inline-block px-5 py-2.5 bg-[#3a2359] text-white text-sm hover:bg-[#4d2f75]"
            data-testid={`give-contact-btn-${slug}`}
          >
            Contact the Parish Office
          </Link>
        </div>
      </section>
    </>
  );
};

export default GivePage;
